import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Link,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import {
  Disclose,
  EmptyState,
  Figure,
  Figures,
  Headline,
  Loading,
  Refusal,
  Stat,
  Stats,
  Tooltip,
} from "@lopie/kernel-ui";

import AgentDrawer from "../components/AgentDrawer.jsx";

/**
 * The org chart — who is acting inside this kernel, and what they did.
 *
 * ## Why this is a chart and not a list
 *
 * The ask this screen answers is a leader's: *show me my team, then show me
 * what they are working on.* That is an org chart, and a table of names is
 * the thing an org chart is not — it flattens the two facts a chart carries
 * for free, which are **who reports into what** and **who is missing**.
 *
 * So the unit is a lane and the lane is a division, and the apps that declare
 * no agent are drawn rather than filtered out. An org chart of one agent is a
 * thin picture; an org chart of eight apps, one of which has an agent in it,
 * is the true picture and reads as a company rather than as a demo.
 *
 * **Two slices, because they answer different questions.** By division is the
 * org: who works where. By app is the estate: which surfaces run an agent at
 * all. An app is used across divisions and a division spans apps, so folding
 * either into the other loses a real answer.
 *
 * ## The card is a summary and the drawer is the record
 *
 * This shipped with everything on the card: the manifest in chips, the model,
 * the tool list, a control that unrolled a three-thousand-character system
 * prompt, and four captions explaining what each number was of. All of it
 * true, and the page was eight screens tall for two agents.
 *
 * A person opening this asks four things in order — *who is this, is it
 * working, what does it cost, is it allowed to write*. Those four are the
 * card. Everything else is one click in, because the detail exists to be
 * weighed, and a paragraph somebody scrolls past has not been weighed.
 *
 * That is not a licence to drop the qualifications. Every number on the card
 * carries its basis **in its own label** — `cost · at list`, `writes · in
 * this window` — so the short version is already true, and the long version
 * is a tooltip or a click away rather than a paragraph in between.
 *
 * ## Every number on a card is a different kind of claim
 *
 * This is the part worth being pedantic about, because a card puts them side
 * by side and side-by-side reads as *the same kind of fact*.
 *
 * - **What it may write** and **what it may read** come from the manifest.
 *   They are complete, they are decisions somebody reviewed, and they are
 *   true whether or not the agent has ever run.
 * - **In this window** and **last write** come from the audit log, and only
 *   from the rows this page actually read. The endpoint pages and bounds its
 *   walk. A card that turned one page into "this agent has never written
 *   anything" would be the exact overstatement this product exists to
 *   prevent, so the card says *in this window* on its face and never "ever".
 * - **Cost** is neither. It is arithmetic on usage Anthropic reported,
 *   priced at list — not a bill, and the label says so.
 *
 * An agent with nothing in the window is therefore `quiet`, not `idle` and
 * certainly not `never ran` — and the word is on the chip, not just the
 * colour.
 *
 * ## The rails are read from the kernel
 *
 * `does_not_prove`, `discloses` and `cost` are shipped by `GET /audit` and
 * rendered from the response. The previous version of this screen wrote its
 * own version of those sentences, which is a second copy of a governance
 * document and wrong the first time somebody changes the real one.
 */

/**
 * How many recent rows to look at.
 *
 * Not "the log". Named on screen every place a count derived from it appears.
 */
const WINDOW = 200;

const BY_DIVISION = "division";
const BY_APP = "app";

/** `division` is a free string on a manifest. Nothing here folds it. */
const NO_DIVISION = "No division named";

function relative(iso) {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

function Mono({ children, ...rest }) {
  return (
    <Typography variant="mono" component="span" {...rest}>
      {children}
    </Typography>
  );
}

/** Thousands read as 2,308 rather than 2308. Counted things want commas. */
const count = (n) => Number(n || 0).toLocaleString("en-US");

/** `$1.05`, and `<$0.01` rather than `$0.00` for a run that did cost money. */
function dollars(usd) {
  if (!usd) return "$0.00";
  return usd < 0.01 ? "<$0.01" : `$${usd.toFixed(2)}`;
}

/**
 * The three states an agent is in, as a word and a colour in that order.
 *
 * `reads only` is the one worth having. An agent whose app declares no action
 * **cannot** write — the kernel refuses the verb, not the record — so
 * rendering it as `quiet` beside one that simply has not run yet says the
 * same thing about two completely different facts. One is a schedule that has
 * not fired; the other is a permission that does not exist.
 */
function statusOf(readOnly, activity) {
  if (readOnly) {
    return {
      word: "reads only",
      tone: "permitted",
      why: "This app declares no action at all, so the kernel refuses every write verb for it. Not a setting and not a habit — there is no verb it could call.",
    };
  }
  if (activity.count) {
    return {
      word: "acting",
      tone: "unattended",
      why: `Named on ${activity.count} write${
        activity.count === 1 ? "" : "s"
      } in the ${activity.window} rows this page read. Not a count over the whole log.`,
    };
  }
  return {
    word: "quiet here",
    tone: "stale",
    why: `Named on no write in the ${activity.window} rows this page read. That is this window, not the log, and not a statement that it has never written.`,
  };
}

/**
 * One agent, at a glance.
 *
 * Name, one sentence, three numbers, and what it is allowed to do in a line.
 * The whole card is the control that opens the rest.
 */
function AgentCard({ agent, app, activity, onOpen }) {
  const last = activity.rows[0];
  const seen = relative(last?.at);
  // The app's own declaration, not a guess from an empty activity list.
  const readOnly = !app?.actions?.length;
  const status = statusOf(readOnly, activity);
  const spend = agent.spend;
  const config = agent.config;

  return (
    // A button, not a div with a click handler. The whole card is the target
    // — a person in a chart is a thing you click on — and making it a button
    // is what puts it in the tab order and gives it a focus ring, which is
    // the difference between "interactive" and "interactive with a mouse".
    <Paper
      component="button"
      type="button"
      onClick={() => onOpen(agent)}
      aria-label={`Open ${agent.name}`}
      variant="outlined"
      sx={{
        p: 0,
        minWidth: 0,
        width: "100%",
        display: "block",
        font: "inherit",
        color: "inherit",
        textAlign: "left",
        cursor: "pointer",
        overflow: "hidden",
        backgroundColor: "background.paper",
        transition: "border-color 120ms",
        "&:hover": { borderColor: "primary.main" },
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      }}
    >
      {/* The one lift on this screen, and it is a spine rather than a ring.
          A card outlined entirely in the status colour reads as an alert; a
          rail down its edge reads as a state, which is what this is. */}
      <Box sx={{ display: "flex", minWidth: 0 }}>
        <Box
          aria-hidden
          sx={{ width: 3, flexShrink: 0, bgcolor: `kernel.${status.tone}` }}
        />
        <Box sx={{ p: 2, minWidth: 0, flex: 1 }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "baseline",
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <Mono sx={{ fontSize: "0.95rem", fontWeight: 600 }}>
              {agent.name}
            </Mono>
            {/* Colour is never the only signal: the word is the label. */}
            <Tooltip title={status.why}>
              <Typography
                variant="overline"
                sx={{ color: `kernel.${status.tone}`, cursor: "help" }}
              >
                {status.word}
              </Typography>
            </Tooltip>
          </Stack>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.75,
              // Two lines. A purpose sentence that runs to six lines is a
              // sentence nobody reads on a card, and the whole of it is in
              // the drawer.
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {agent.does}
          </Typography>

          {/* Tighter than the page-level row. Three stats in a card this
              wide fit on one line at 16px of gap and wrap two-and-one at 24,
              and a stat orphaned on its own row reads as a fourth thing. */}
          <Stats sx={{ mt: 1.75, columnGap: 2 }}>
            <Stat
              value={
                spend?.runs
                  ? `${spend.complete ? "" : "≥ "}${dollars(spend.usd)}`
                  : "—"
              }
              label="cost · at list"
              tone={spend?.complete === false ? "attention" : undefined}
              title={
                spend?.runs
                  ? `${spend.basis}${
                      spend.complete
                        ? ""
                        : " At least one run used a model this kernel holds no price for, so this is a floor rather than a sum."
                    }`
                  : "No run has been recorded through the kernel, so there is nothing to price. Not the same as never having run."
              }
            />
            <Stat
              value={spend?.runs ? count(spend.runs) : "—"}
              label="runs · recorded"
              title="Runs this kernel recorded. Anything driven from a terminal before the kernel was in the path left a row in the audit log and none here."
            />
            <Stat
              value={activity.count ? count(activity.count) : "0"}
              label="writes · in this window"
              title={status.why}
            />
          </Stats>

          {/* One line, and it is the answer to "what is it allowed to do".
              The lists themselves are in the drawer — three chips and a
              wrapped row of grants is the thing that made this card tall. */}
          <Stack
            direction="row"
            spacing={0.75}
            sx={{
              mt: 1.75,
              pt: 1.25,
              borderTop: 1,
              borderColor: "divider",
              flexWrap: "wrap",
              alignItems: "baseline",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {readOnly
                ? "Writes nothing"
                : `Writes ${app.actions.length} verb${
                    app.actions.length === 1 ? "" : "s"
                  }`}
              {" · "}
              {app?.service_reads?.length
                ? `reads ${app.service_reads.length} type${
                    app.service_reads.length === 1 ? "" : "s"
                  } unattended`
                : "reads nothing unattended"}
              {config?.model ? ` · ${config.model}` : " · not configured"}
              {seen ? ` · last ${seen}` : null}
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Paper>
  );
}

/**
 * One lane — a division, or an app, depending on the slice.
 *
 * ## Why lanes are columns now
 *
 * Each lane used to be a full-width band, so two divisions with one agent
 * each produced two bands of one card and a page of mostly empty paper. The
 * lanes are a board: each is a column in one grid, so the chart is as tall as
 * its busiest lane rather than as tall as the sum of all of them, and two
 * small teams read as two small teams side by side.
 *
 * The header carries the two counts a leader reads first and nothing else.
 */
function Lane({ name, caption, agents, apps, activityFor, onOpen }) {
  const writes = agents.reduce(
    (total, agent) => total + activityFor(agent).count,
    0,
  );

  return (
    // The cap that the grid track cannot carry. A single division on a wide
    // screen gets a readable column, not a card eleven hundred pixels wide.
    <Box sx={{ minWidth: 0, maxWidth: 560 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "baseline",
          justifyContent: "space-between",
          flexWrap: "wrap",
          mb: 1,
          pb: 0.5,
          borderBottom: 2,
          borderColor: "primary.main",
        }}
      >
        <Typography variant="h2" sx={{ overflowWrap: "anywhere" }}>
          {name}
        </Typography>
        <Tooltip title="Writes naming an agent in this lane, out of the rows this page read. Not a count over the whole log.">
          <Typography variant="body2" color="text.secondary" sx={{ cursor: "help" }}>
            {agents.length} agent{agents.length === 1 ? "" : "s"} · {writes}{" "}
            write{writes === 1 ? "" : "s"}
          </Typography>
        </Tooltip>
      </Stack>
      {caption ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ display: "block", mb: 1 }}
        >
          {caption}
        </Typography>
      ) : null}
      <Stack spacing={1.5} sx={{ minWidth: 0 }}>
        {agents.map((agent) => (
          <AgentCard
            key={`${agent.app_type}:${agent.name}`}
            agent={agent}
            app={apps.find((a) => a.app_type === agent.app_type)}
            activity={activityFor(agent)}
            onOpen={onOpen}
          />
        ))}
      </Stack>
    </Box>
  );
}

/**
 * That connectors wrote most of this, and where to go to see it.
 *
 * ## Why the cards went away
 *
 * They were right when they landed. The chart drew two agents and eight apps
 * while a connector that had written 2,308 of the records in this kernel was
 * nowhere on the page, and an org chart of a company that leaves out the
 * thing doing most of the work is not a chart of that company.
 *
 * What changed is that **Wiring exists now**, and its whole question is where
 * an agent's data comes from — connectors are its first column, drawn into
 * the types they feed and onward to the apps and agents that read them. Six
 * connector cards on this screen were the same facts with the downstream half
 * missing, on a screen that asks who *acts*.
 *
 * So the fact stays and the cards go. One sentence, because dropping it
 * entirely would put the original omission straight back.
 */
function FromConnectors({ writers }) {
  const connectors = (writers || []).filter((w) => w.kind === "connector");
  if (!connectors.length) return null;
  const records = connectors.reduce((total, w) => total + (w.total || 0), 0);

  return (
    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "70ch" }}>
      {count(records)} of the records in this kernel were written by{" "}
      {connectors.length}{" "}
      {connectors.length === 1 ? "connector" : "connectors"} rather than by
      anyone on this page — a connector has no manifest, no sign-in and no
      agent, and it is doing most of the writing.{" "}
      <Link
        component="button"
        variant="body2"
        onClick={() => {
          window.location.hash = "#wiring";
        }}
        sx={{ verticalAlign: "baseline" }}
      >
        See what they feed
      </Link>
      .
    </Typography>
  );
}


/**
 * A heading, its count, and one sentence with the rest behind a control.
 *
 * Every section on this screen had a paragraph under its title. Four of them
 * is four paragraphs a person scrolls past to reach the cards, so the first
 * sentence stays and the argument goes one click in.
 */
function SectionHead({ title, meta, summary, detail, muted }) {
  return (
    <Box sx={{ mb: 1.5, minWidth: 0 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "baseline",
          justifyContent: "space-between",
          flexWrap: "wrap",
          pb: 0.5,
          borderBottom: muted ? 1 : 2,
          borderColor: muted ? "divider" : "primary.main",
        }}
      >
        <Typography variant="h2" color={muted ? "text.secondary" : undefined}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {meta}
        </Typography>
      </Stack>
      {summary ? (
        <Box sx={{ mt: 0.75 }}>
          {detail ? (
            <Disclose summary={summary} label="More">
              <Typography variant="body2" color="text.secondary">
                {detail}
              </Typography>
            </Disclose>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {summary}
            </Typography>
          )}
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * The apps with nobody in them.
 *
 * Drawn, not filtered. An org chart that hides the empty desks is a chart
 * that cannot be used to decide anything — and the sentence underneath is the
 * argument this whole product makes, so it stays on the page where the
 * absence is visible, one click in rather than in a footnote somewhere else.
 */
function Unstaffed({ apps }) {
  if (!apps.length) return null;
  return (
    <Box sx={{ minWidth: 0 }}>
      <SectionHead
        title="Staffed by code"
        meta={`${apps.length} app${apps.length === 1 ? "" : "s"} · no agent declared`}
        muted
      />
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", mb: 1 }}>
        {apps.map((app) => (
          <Chip
            key={app.app}
            size="small"
            variant="outlined"
            label={app.app_type}
            sx={{ fontFamily: "ui-monospace, monospace" }}
          />
        ))}
      </Stack>
      <Disclose
        summary="Every write from these is the app's own code, and their records say so."
        label="What that means"
      >
        <Typography variant="body2" color="text.secondary">
          An absent <Mono>agent</Mono> in the envelope means there was none,
          not that one could not be identified. An app cannot grow an agent by
          being redeployed: it declares one in a reviewed manifest, or in a
          request approved through LO-61, and nowhere else.
        </Typography>
      </Disclose>
    </Box>
  );
}

/**
 * The kernel's sentences, with its backticks rendered as code.
 *
 * `does_not_prove` and `cost` are prose written in the kernel and they use
 * backticks the way the rest of this repo does — `agent`, `?mode=`. Printing
 * them as literal backtick characters is the small sloppiness that makes a
 * screen look generated, and rewriting the sentences to remove them would be
 * keeping a second copy of a governance document, which is the larger
 * mistake. So the text is the kernel's, and only the delimiter is ours.
 */
function Prose({ children }) {
  // Elements pass through untouched. This takes a string and turns backticked
  // spans into `Mono`, and `String(children)` on anything else produces
  // comma-joined text with `[object Object]` where each element was — which
  // is what it did, on screen, rather than failing. A helper whose wrong
  // input renders as garbage instead of throwing is a helper that needs the
  // guard rather than a note in its docstring.
  if (typeof children !== "string") return children;
  const parts = children.split("`");
  return (
    <>
      {parts.map((part, index) =>
        index % 2 ? (
          <Mono key={index}>{part}</Mono>
        ) : (
          <span key={index}>{part}</span>
        ),
      )}
    </>
  );
}

export default function Agents({ get }) {
  const [page, setPage] = useState(null);
  const [apps, setApps] = useState(null);
  const [writers, setWriters] = useState(null);
  const [roster, setRoster] = useState(null);
  const [failure, setFailure] = useState(null);
  const [slice, setSlice] = useState(BY_DIVISION);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setFailure(null);
    try {
      // Two registers, both already permitted to this app, and neither one
      // substitutable for the other: `/apps` is the org and is complete;
      // `/audit` is the activity and is a window. Reading the chart out of
      // the audit vocabulary alone would lose every app that has no agent,
      // which is most of the picture.
      const [audit, register, writers, roster] = await Promise.all([
        get("/audit", { limit: WINDOW, sort: "desc" }),
        get("/apps"),
        // The third register, and the one this screen used to leave out.
        // Gated on having a manifest like `/apps`, so Agent OS already had
        // it; nothing was widened to draw this.
        get("/connectors"),
        // The fourth, and it is the reason the card can show what an agent
        // was *configured* with (LO-110). `/apps` is one row per manifest and
        // is the ceiling; this is one row per agent and carries the other
        // half — the model it thinks with, the prompt it was given, and what
        // its runs have cost.
        //
        // The chart is still drawn from `/apps`, deliberately. An agent this
        // register did not return must still appear on the chart, because
        // "declared in a manifest and not configured" is a real state and the
        // screen says so; drawing from here instead would make a missing
        // config look like a missing agent.
        get("/agents"),
      ]);
      setPage(audit);
      setApps((register.apps || []).filter((a) => !a.unparseable));
      setWriters(writers);
      setRoster(roster);
    } catch (e) {
      setPage(null);
      setFailure(e);
    }
  }, [get]);

  useEffect(() => {
    load();
  }, [load]);

  const items = useMemo(() => page?.items || [], [page]);

  /**
   * Rows naming one agent, newest first.
   *
   * Keyed by `app_type:name` and not by name alone — two apps may each
   * declare an agent called `sync`, and the envelope carries both fields
   * precisely so they do not have to be the same thing.
   */
  const activity = useMemo(() => {
    const byAgent = new Map();
    for (const row of items) {
      const named = row.provenance?.agent;
      if (!named) continue;
      const key = `${row.provenance?.app_type}:${named}`;
      if (!byAgent.has(key)) byAgent.set(key, []);
      byAgent.get(key).push(row);
    }
    return byAgent;
  }, [items]);

  const activityFor = useCallback(
    (agent) => {
      const rows = activity.get(`${agent.app_type}:${agent.name}`) || [];
      return { rows, count: rows.length, window: items.length };
    },
    [activity, items],
  );

  if (failure) {
    return <Refusal failure={failure} actor="Agent OS" onRetry={load} />;
  }
  if (!page || !apps || !writers || !roster) {
    return <Loading label="Drawing the org chart" />;
  }

  // Keyed `app_type:name` for the reason `activity` is: two apps may each
  // declare an agent called `sync`, and only the pair identifies one.
  const configured = new Map(
    (roster.agents || []).map((a) => [`${a.app_type}:${a.name}`, a]),
  );

  // `app_type` and not `app`. `/apps` keys a row by the **Cognito client
  // id**, which is what makes a manifest mean anything and is unreadable in a
  // chart; `app_type` is the name a person wrote. It is also the field the
  // provenance envelope carries, so joining activity on the client id finds
  // nothing at all — which is how the first version of this screen showed an
  // agent as `quiet here` on the same page that said one write named it.
  const declared = apps.flatMap((app) =>
    (app.agents || []).map((agent) => {
      const extra = configured.get(`${app.app_type}:${agent.name}`) || {};
      return {
        ...agent,
        app_type: app.app_type,
        config: extra.config ?? null,
        spend: extra.spend ?? null,
      };
    }),
  );
  const unstaffed = apps.filter((app) => !(app.agents || []).length);
  const attributed = items.filter((row) => row.provenance?.agent).length;
  const connectorCount = (writers.writers || []).filter(
    (w) => w.kind === "connector",
  ).length;
  const spent = declared.reduce((total, a) => total + (a.spend?.usd || 0), 0);
  const runs = declared.reduce((total, a) => total + (a.spend?.runs || 0), 0);

  if (!declared.length) {
    return (
      <Stack spacing={2}>
        <EmptyState title="No app in this kernel declares an agent">
          <Stack spacing={1.5}>
            <Typography variant="body2">
              This is not a page that failed to load and it is not a permission
              problem. Agent OS reads the org out of every app&apos;s manifest
              and no manifest names an agent, so there is no chart to draw —
              every write in this kernel is an app&apos;s own code, and its
              records say so.
            </Typography>
            <Typography variant="body2">
              An app cannot grow an agent by being redeployed. It declares one
              in its committed manifest, or in a manifest request approved
              through LO-61, and both are reviewed by a person. That is why
              this screen can be empty and still be correct.
            </Typography>
          </Stack>
        </EmptyState>
        <Unstaffed apps={unstaffed} />
      </Stack>
    );
  }

  // Insertion order, which is the order the manifests were read. Sorting
  // would reorder somebody's reviewed document on the way to the screen.
  const lanes = [];
  for (const agent of declared) {
    const key =
      slice === BY_DIVISION ? agent.division || NO_DIVISION : agent.app_type;
    const found = lanes.find((lane) => lane.name === key);
    if (found) found.agents.push(agent);
    else lanes.push({ name: key, agents: [agent] });
  }

  return (
    <Stack spacing={3}>
      {/* The whole org in one row of figures. This was a sentence, and a
          sentence is the wrong shape for four numbers somebody wants to
          compare with last week's four numbers. */}
      {/* Money is the figure. Everything else on this screen is a count of
          declarations, and a declaration costs nothing — what an agent has
          actually spent running unattended is the number somebody wants
          before they let another one loose.

          The rest stay as figures rather than as a second headline: four
          display sizes on one page is four headlines and therefore none. */}
      <Headline
        eyebrow="Spent by agents, at list prices"
        figure={dollars(spent)}
        caption={`across ${runs} recorded run${runs === 1 ? "" : "s"}`}
        basis="Computed from each session's reported usage at list prices, not read from a bill. Anything driven from a terminal before the kernel was in the path left a row in the audit log and none here, so this is what the kernel recorded rather than everything that ran."
        right={
          <ToggleButtonGroup
            size="small"
            exclusive
            value={slice}
            onChange={(_event, next) => next && setSlice(next)}
            aria-label="How to group the chart"
            sx={{ flexShrink: 0 }}
          >
            <ToggleButton value={BY_DIVISION}>By division</ToggleButton>
            <ToggleButton value={BY_APP}>By app</ToggleButton>
          </ToggleButtonGroup>
        }
      >
        <Figures sx={{ mt: 2.5 }}>
          <Figure
            value={count(declared.length)}
            label={`agent${declared.length === 1 ? "" : "s"} · declared`}
            basis="Declared in a committed manifest somebody reviewed. True whether or not any of them has ever run."
          />
          <Figure
            value={count(apps.length)}
            label="apps"
            basis="Every app this kernel holds a manifest for, agent or not."
          />
          <Figure
            value={count(connectorCount)}
            label="connectors"
            basis="Neither agents nor apps. No manifest and no Cognito client — they are here because they wrote."
          />
          <Figure
            value={`${attributed}/${items.length}`}
            label="writes · name an agent"
            basis={`Of the ${items.length} most recent writes this page read. A window, not the log.`}
          />
        </Figures>
      </Headline>

      {/* A board. Lanes are columns, so the page is as tall as the busiest
          lane rather than as tall as all of them stacked. */}
      <Box
        sx={{
          display: "grid",
          gap: { xs: 2.5, md: 3 },
          // `auto-fit`, not `auto-fill`. `auto-fill` keeps the empty tracks
          // it made room for, which is why two divisions sat in the left two
          // thirds of the page with a third of it blank. `auto-fit` collapses
          // the empty ones and the lanes grow into the space.
          //
          // The cap is on the lane and not on the track, which took a
          // measurement to get right. `minmax(300px, 560px)` looks like "at
          // least 300, at most 560" and is not: the repeat count is computed
          // from the *max* when the max is a definite length, so an 1,137px
          // container fits one 560px track and the board rendered a single
          // column. `1fr` here and `maxWidth` on the lane gives both halves —
          // as many tracks as fit, and no lane stretched to the width of the
          // screen to hold forty characters of name.
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(auto-fit, minmax(300px, 1fr))",
          },
          alignItems: "start",
        }}
      >
        {lanes.map((lane) => (
          <Lane
            key={lane.name}
            name={lane.name}
            caption={
              slice === BY_DIVISION && lane.name === NO_DIVISION
                ? "These agents name no division. Nothing here invents one for them."
                : undefined
            }
            agents={lane.agents}
            apps={apps}
            activityFor={activityFor}
            onOpen={setOpen}
          />
        ))}
      </Box>

      <FromConnectors writers={writers.writers || []} />

      <Unstaffed apps={unstaffed} />

      {/* This one stays a visible alert rather than folding into the footer.
          A walk that ran out of budget changes what every count above it
          means, and that is a live condition rather than a standing caveat. */}
      {page.stopped_early ? (
        <Alert
          severity="warning"
          variant="outlined"
          sx={{ borderColor: "kernel.attention" }}
        >
          <AlertTitle>This window stopped early</AlertTitle>
          <Prose>{page.stopped_detail}</Prose>
        </Alert>
      ) : null}

      <AgentDrawer
        agent={open}
        app={apps.find((a) => a.app_type === open?.app_type)}
        activity={open ? activityFor(open) : null}
        get={get}
        onClose={() => setOpen(null)}
      />
    </Stack>
  );
}
